import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { agreements, type AgreementScope, type SharedRepository } from '../../api/agreements';
import { teams as teamsApi } from '../../api/organization';
import { m } from 'motion/react';

import { ApiError } from '../../api/problem';
import { Button } from '../../components/Button';
import { fieldStyles } from '../../components/styles';
import { useI18n, useT } from '../../i18n/provider';
import { cn } from '../../lib/cn';

/**
 * The proposal assistant of `07 §6.2`, as one grouped form: counterpart,
 * own communication teams, scope, terms, validity, and a summary line that
 * says what the agents of both sides will read. The six steps of the contract
 * are the six fieldsets — a manager who can see the whole document makes
 * fewer half-configured agreements than one walked through it blind.
 */
export function ProposalWizard({
  companyId,
  onDone,
  onCancel,
}: {
  companyId: string;
  onDone: (id: string) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const { plural } = useI18n();
  const client = useQueryClient();
  const [counterpartId, setCounterpartId] = useState('');
  const [title, setTitle] = useState('');
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [topics, setTopics] = useState('integration');
  const [delegation, setDelegation] = useState(false);
  const [repoSharing, setRepoSharing] = useState(false);
  const [sharedRepos, setSharedRepos] = useState('');
  const [maxPerDay, setMaxPerDay] = useState('100');
  const [terms, setTerms] = useState('');
  const [confidentiality, setConfidentiality] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const directory = useQuery({ queryKey: ['companies-directory'], queryFn: agreements.directory });
  const ownTeams = useQuery({
    queryKey: ['teams', companyId],
    queryFn: () => teamsApi.list(companyId),
    enabled: companyId !== '',
  });

  const communicationTeams = useMemo(
    () => (ownTeams.data?.data ?? []).filter((team) => team.type === 'communication' && team.archived_at === null),
    [ownTeams.data],
  );

  const counterparts = (directory.data?.data ?? []).filter((company) => company.id !== companyId);
  const topicList = topics
    .split(',')
    .map((topic) => topic.trim())
    .filter((topic) => topic !== '');

  const create = useMutation({
    mutationFn: () => {
      const scope: AgreementScope = {
        allowed_topics: topicList,
        allow_task_delegation: delegation,
        allow_repository_sharing: repoSharing,
        message_limits: { max_per_day: Number(maxPerDay) || 100 },
      };

      if (repoSharing && sharedRepos.trim() !== '') {
        scope.shared_repositories = sharedRepos
          .split(',')
          .map((entry) => entry.trim())
          .filter((entry) => entry.includes('/'))
          .map((entry): SharedRepository => {
            const [companySlug, rest] = entry.split('/', 2);
            const [repositorySlug, access] = (rest ?? '').split(':', 2);
            return {
              company_slug: companySlug ?? '',
              repository_slug: repositorySlug ?? '',
              access: access === 'write' ? 'write' : 'read',
            };
          });
      }

      if (confidentiality.trim() !== '') {
        scope.confidentiality_note = confidentiality.trim();
      }

      return agreements.create(companyId, {
        counterpart_company_id: counterpartId,
        title,
        terms: terms.trim() === '' ? null : terms,
        scope,
        team_ids: teamIds,
        valid_from: null,
        valid_until: validUntil === '' ? null : new Date(`${validUntil}T23:59:59Z`).toISOString(),
      });
    },
    onSuccess: (draft) => {
      void client.invalidateQueries({ queryKey: ['agreements', companyId] });
      onDone(draft.id);
    },
    onError: (error) => {
      setProblem(error instanceof ApiError ? (error.problem.detail ?? error.message) : t('proposal.draftFailed'));
    },
  });

  const counterpartName = counterparts.find((company) => company.id === counterpartId)?.name ?? t('proposal.theCounterpart');

  return (
    <form
      className="max-w-2xl space-y-6"
      onSubmit={(event) => {
        event.preventDefault();
        setProblem(null);
        create.mutate();
      }}
    >
      <h2 className="text-xl font-semibold tracking-tight text-ink">{t('proposal.title')}</h2>
      {problem !== null && (
        <p role="alert" className="animate-rise rounded-md border border-danger-line bg-danger-pale p-3 text-sm text-ink">
          {problem}
        </p>
      )}

      <fieldset className="space-y-2">
        <legend className="text-sm font-semibold text-ink">{t('proposal.step1')}</legend>
        <select
          aria-label={t('proposal.counterpartLabel')}
          className={cn(fieldStyles, 'border-line')}
          value={counterpartId}
          onChange={(event) => setCounterpartId(event.target.value)}
          required
        >
          <option value="">{t('proposal.chooseCompany')}</option>
          {counterparts.map((company) => (
            <option key={company.id} value={company.id}>
              {/* Name and slug: names are not unique across a deployment,
                  and proposing to the wrong same-named company is exactly the
                  mistake a directory should make impossible. */}
              {company.name} — {company.slug}
            </option>
          ))}
        </select>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-sm font-semibold text-ink">{t('proposal.step2')}</legend>
        {communicationTeams.length === 0 ? (
          <p className="text-sm leading-relaxed text-ink-soft">{t('proposal.noTeams')}</p>
        ) : (
          communicationTeams.map((team) => (
            <label key={team.id} className="flex cursor-pointer items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={teamIds.includes(team.id)}
                onChange={(event) =>
                  setTeamIds((current) =>
                    event.target.checked ? [...current, team.id] : current.filter((id) => id !== team.id),
                  )
                }
              />
              {team.name} · {plural('proposal.members', team.member_count)}
            </label>
          ))
        )}
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold text-ink">{t('proposal.step3')}</legend>
        <label className="block text-sm text-ink">
          {t('proposal.agreementTitle')}
          <input
            className={cn(fieldStyles, 'mt-1.5 border-line')}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={160}
            required
          />
        </label>
        <label className="block text-sm text-ink">
          {t('proposal.topics')}
          <input
            className={cn(fieldStyles, 'mt-1.5 border-line')}
            value={topics}
            onChange={(event) => setTopics(event.target.value)}
            placeholder="integration, api-contract, incident"
          />
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="size-4 accent-primary"
            checked={delegation}
            onChange={(event) => setDelegation(event.target.checked)}
          />
          {t('proposal.allowDelegation')}
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="size-4 accent-primary"
            checked={repoSharing}
            onChange={(event) => setRepoSharing(event.target.checked)}
          />
          {t('proposal.allowRepoSharing')}
        </label>
        {repoSharing && (
          <label className="block text-sm text-ink">
            {t('proposal.sharedRepos')}
            <input
              className={cn(fieldStyles, 'mt-1.5 border-line')}
              value={sharedRepos}
              onChange={(event) => setSharedRepos(event.target.value)}
              placeholder="acme/acme-webshop-api:read"
            />
          </label>
        )}
        <label className="block text-sm text-ink">
          {t('proposal.messagesPerDay')}
          <input
            type="number"
            min={1}
            className={cn(fieldStyles, 'mt-1.5 w-32 border-line')}
            value={maxPerDay}
            onChange={(event) => setMaxPerDay(event.target.value)}
          />
        </label>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold text-ink">{t('proposal.step4')}</legend>
        <textarea
          aria-label={t('proposal.terms')}
          className={cn(fieldStyles, 'resize-y border-line')}
          rows={3}
          value={terms}
          onChange={(event) => setTerms(event.target.value)}
          placeholder={t('proposal.termsPlaceholder')}
        />
        <label className="block text-sm text-ink">
          {t('proposal.confidentiality')}
          <textarea
            className={cn(fieldStyles, 'mt-1.5 resize-y border-line')}
            rows={2}
            value={confidentiality}
            onChange={(event) => setConfidentiality(event.target.value)}
          />
        </label>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-sm font-semibold text-ink">{t('proposal.step5')}</legend>
        <label className="block text-sm text-ink">
          {t('proposal.validUntil')}
          <input
            type="date"
            className={cn(fieldStyles, 'mt-1.5 w-auto border-line')}
            value={validUntil}
            onChange={(event) => setValidUntil(event.target.value)}
          />
        </label>
      </fieldset>

      {/* El resumen se recompone en cada pulsación: es lo que convierte cinco
          formularios en una frase que se puede leer antes de enviar. */}
      <m.div
        layout
        className="rounded-md border border-primary/25 bg-primary-pale/40 p-4 text-sm text-ink-soft"
      >
        <p className="font-semibold text-ink">{t('proposal.step6')}</p>
        <p className="mt-1.5 leading-relaxed">
          {t('proposal.summary', {
            counterpart: counterpartName,
            topics: topicList.length === 0 ? t('proposal.noTopics') : topicList.join(', '),
            delegation: delegation ? t('proposal.allowed') : t('proposal.notAllowed'),
            repos: repoSharing ? t('proposal.shared') : t('proposal.notShared'),
          })}
        </p>
      </m.div>

      <div className="flex gap-2">
        <Button type="submit" busy={create.isPending}>
          {t('proposal.createDraft')}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}
